import { Loader, Text } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'hooks/useAuth';
import React, { useEffect } from 'react';
import { HiOutlineExternalLink } from 'react-icons/hi';
import { beelajarService } from 'services/beelajar/beelajar.service';
import { showErrorNotification } from 'utils/notification';

interface MaterialLinkProps {
  linkMaterial: string;
  title?: string;
  showIcon?: boolean;
  isBeelajarLoginLink?: boolean;
}

const MaterialLink: React.FC<MaterialLinkProps> = ({
  linkMaterial,
  title,
  showIcon = true,
  isBeelajarLoginLink = false
}) => {
  const { profile } = useAuth();
  const { hovered, ref } = useHover();

  const beelajarLogin = useMutation({
    mutationFn: beelajarService.loginBeelajarService
  });

  const {
    data: dataBeelajarLogin,
    isPending: isLoadingBeelajarLogin,
    isError: isErrorBeelajarLogin,
    isSuccess: isSuccessBeelajarLogin
  } = beelajarLogin;

  useEffect(() => {
    if (isErrorBeelajarLogin || (isSuccessBeelajarLogin && !dataBeelajarLogin?.data?.loginurl)) {
      showErrorNotification({
        title: 'Error Unauthorized Access',
        message: 'Failed to login beelajar lms'
      });
    }
    if (!isErrorBeelajarLogin && dataBeelajarLogin?.data?.loginurl) {
      const url = dataBeelajarLogin?.data?.loginurl + '&wantsurl=' + linkMaterial;
      window.open(url, '_blank');
    }
  }, [isErrorBeelajarLogin, isSuccessBeelajarLogin, dataBeelajarLogin]);

  const handleBeelajarLogin = async () => {
    if (!profile) return;
    await beelajarLogin.mutateAsync({ email: profile.beelajarEmail || profile.adminEmail });
    setTimeout(async () => {
      await beelajarLogin.mutateAsync({ email: profile.beelajarEmail || profile.adminEmail });
    }, 500);
  };

  if (!title && !linkMaterial)
    return (
      <Text fz={'sm'} fw={600}>
        -
      </Text>
    );

  if (isBeelajarLoginLink) {
    return (
      <>
        <Text
          ref={ref}
          fz={'sm'}
          fw={600}
          style={{ cursor: 'pointer' }}
          onClick={handleBeelajarLogin}
          td={'underline'}
          c={hovered ? 'primary' : 'dark'}
        >
          {isLoadingBeelajarLogin && <Loader size={12} />}
          {title || linkMaterial}
          {showIcon && hovered && <HiOutlineExternalLink style={{ marginLeft: 4 }} />}
        </Text>
        {isErrorBeelajarLogin && <Text c='destructive'>Failed to login beelajar lms</Text>}
      </>
    );
  }

  return (
    <Text
      ref={ref}
      fz={'sm'}
      fw={600}
      component='a'
      href={linkMaterial}
      target='_blank'
      td={'underline'}
      c={hovered ? 'primary' : 'dark'}
    >
      {title || linkMaterial}
      {showIcon && hovered && <HiOutlineExternalLink style={{ marginLeft: 4 }} />}
    </Text>
  );
};

export default MaterialLink;
